import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, GrGameConfigDetail, GrGameConfigReviewStatus } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';

interface UseGameConfigsOptions {
  reviewStatus?: GrGameConfigReviewStatus;
  /** Include soft-deleted rows alongside live ones. */
  includeDeleted?: boolean;
  /** Return ONLY soft-deleted rows. Drives the admin "Deleted" filter. */
  onlyDeleted?: boolean;
  /** When false, the underlying query stays paused. Use for lazy-loaded
   *  surfaces like the clone-source dropdown (review I15). */
  enabled?: boolean;
}

interface UseGameConfigsReturn {
  gameConfigs: GrGameConfigDetail[];
  total: number;
  loading: boolean;
  error: string | null;
  refresh: () => void;
}

export function useGameConfigs(options?: UseGameConfigsOptions): UseGameConfigsReturn {
  const filters: {
    reviewStatus?: GrGameConfigReviewStatus;
    includeDeleted?: boolean;
    onlyDeleted?: boolean;
  } = {};
  if (options?.reviewStatus) filters.reviewStatus = options.reviewStatus;
  if (options?.includeDeleted) filters.includeDeleted = true;
  if (options?.onlyDeleted) filters.onlyDeleted = true;
  const hasFilters = Object.keys(filters).length > 0;
  const enabled = options?.enabled ?? true;
  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: queryKeys.gameRewards.gameConfigs(hasFilters ? filters : undefined),
    queryFn: () => api.listGameRewardsGames(hasFilters ? filters : undefined),
    staleTime: 5 * 60 * 1000, // Game configs are admin-controlled, rarely change
    enabled,
  });

  return {
    gameConfigs: data?.gameConfigs ?? [],
    total: data?.total ?? 0,
    loading: isLoading,
    error: error instanceof Error ? error.message : isError ? 'Failed to load game configs' : null,
    refresh: () => {
      refetch();
    },
  };
}

interface ApproveGameMutationVars {
  gameConfigId: string;
}

interface RejectGameMutationVars {
  gameConfigId: string;
  reason?: string;
}

/**
 * Mutations for the admin approval flow. Invalidate the game-config list on
 * success so any open list view refreshes immediately.
 */
export function useApproveGameConfig() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ gameConfigId }: ApproveGameMutationVars) =>
      api.approveGameRewardsGame(gameConfigId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.gameRewards.gameConfigs() });
    },
  });
}

export function useRejectGameConfig() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ gameConfigId, reason }: RejectGameMutationVars) =>
      api.rejectGameRewardsGame(gameConfigId, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.gameRewards.gameConfigs() });
    },
  });
}

/**
 * Update a game config's settings (the inline editor on AdminGameDetailPage).
 * Cap-increasing edits flip review_status to pending server-side; the
 * mutation invalidates the queries that actually depend on game-config state
 * (review C5 — invalidating gameRewards.all stampedes leaderboards,
 * subscriptions, store-products, claim-rejections, etc. on every save).
 */
export function useUpdateGameConfig() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      gameConfigId,
      input,
    }: {
      gameConfigId: string;
      input: Parameters<typeof api.updateGameRewardsGame>[1];
    }) => api.updateGameRewardsGame(gameConfigId, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.gameRewards.gameConfigs() });
      queryClient.invalidateQueries({ queryKey: queryKeys.gameRewards.rewardRules() });
    },
  });
}

/**
 * Clone an existing game config (from the Register Game dialog).
 * Resets gameName/displayName/api-key/linked-arche/review-status. Scoped
 * invalidation per review C5.
 */
export function useCloneGameConfig() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: Parameters<typeof api.cloneGameRewardsGame>[0]) =>
      api.cloneGameRewardsGame(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.gameRewards.gameConfigs() });
      queryClient.invalidateQueries({ queryKey: queryKeys.gameRewards.rewardRules() });
    },
  });
}

/**
 * Soft-delete a game config. Hides the row from admin lists and the
 * claim path; reversible only by direct DB intervention. For an
 * irreversible removal, see {@link usePurgeGameConfig}.
 */
export function useSoftDeleteGameConfig() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ gameConfigId }: { gameConfigId: string }) =>
      api.deleteGameRewardsGame(gameConfigId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.gameRewards.gameConfigs() });
    },
  });
}

/**
 * Reverse a soft-delete. Idempotent — calling this on a live config
 * returns it unchanged without writing an audit row.
 */
export function useRestoreGameConfig() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ gameConfigId }: { gameConfigId: string }) =>
      api.restoreGameRewardsGame(gameConfigId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.gameRewards.gameConfigs() });
    },
  });
}

/**
 * Hard-delete a game config and all of its children. Irreversible.
 * `confirmGameName` must match the row's current game_name; the server
 * rejects with 409 otherwise. The UI gates this behind a typed-name
 * confirmation field.
 *
 * The `force` flag overrides the server-side row-count guard
 * (rewardLogCount > PURGE_ROW_COUNT_THRESHOLD). Only set true after
 * the admin has explicitly acknowledged the count.
 */
export function usePurgeGameConfig() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      gameConfigId,
      confirmGameName,
      force,
    }: {
      gameConfigId: string;
      confirmGameName: string;
      force?: boolean;
    }) => api.purgeGameRewardsGame(gameConfigId, confirmGameName, force ? { force: true } : {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.gameRewards.gameConfigs() });
      queryClient.invalidateQueries({ queryKey: queryKeys.gameRewards.rewardRules() });
    },
  });
}
